import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Alert, Box, Typography, Divider } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthModal } from '../../contexts/AuthModalContext';
import { useAuth } from '../../contexts/AuthContext';
import API_BASE_URL from '../../config/api';
import { getRedirectPathByEducationLevel } from '../../utils/redirectUtils';
import { isHighSchoolAuthRoute } from '../../utils/authUtils';
import AuthLegalNotice from '../AuthLegalNotice';
import SessionReplacementDialog, { SessionReplacementProof, SessionReplacementProofType } from '../SessionReplacementDialog';

const LoginModal: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [replacementChallenge, setReplacementChallenge] = useState<string | null>(null);
  const [replacementProofType, setReplacementProofType] = useState<SessionReplacementProofType>('password');
  const { loginOpen, closeLogin, switchToSignup } = useAuthModal();
  const { login, confirmSessionReplacement } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isHighSchoolAuthRoute(location.pathname) && loginOpen) {
      closeLogin();
    }
  }, [location.pathname, loginOpen, closeLogin]);

  if (isHighSchoolAuthRoute(location.pathname)) {
    return null;
  }

  const submit = async () => {
    setError(null);
    setLoading(true);
    try {
      const userData = await login(email, password);
      finishLogin(userData);
    } catch (err: any) {
      if (err?.response?.data?.code === 'SESSION_REPLACEMENT_REQUIRED') {
        setReplacementChallenge(err.response.data.challengeToken);
        setReplacementProofType(err.response.data.proofType || 'password');
      } else {
        setError(err?.response?.data?.message || err?.message || 'Login failed');
      }
      setLoading(false);
    }
  };

  const finishLogin = (userData: any) => {
    setLoading(false);
    setReplacementChallenge(null);
    closeLogin();
    setEmail('');
    setPassword('');
    const redirectPath = getRedirectPathByEducationLevel(userData?.educationLevel);
    navigate(redirectPath, { replace: true });
  };

  const confirmReplacement = async (proof: SessionReplacementProof) => {
    if (!replacementChallenge) return;
    setLoading(true);
    setError(null);
    try {
      finishLogin(await confirmSessionReplacement(replacementChallenge, proof));
    } catch (err: any) {
      if (err?.response?.data?.code === 'SESSION_REPLACEMENT_REQUIRED') {
        setReplacementChallenge(err.response.data.challengeToken);
        setReplacementProofType(err.response.data.proofType || replacementProofType);
        setError(null);
      } else if (err?.response?.data?.code === 'INVALID_CONFIRMATION_CREDENTIALS') {
        setError(err.response.data.message || 'The password is incorrect.');
      } else if (err?.response?.data?.code === 'SESSION_REPLACEMENT_EXPIRED') {
        setReplacementChallenge(null);
        setError(err.response.data.message || 'The sign-in confirmation expired. Please sign in again.');
      } else {
        setError(err?.response?.data?.message || err?.message || 'Could not sign in on this device');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    closeLogin();
    setError(null);
    setEmail('');
    setPassword('');
    setReplacementChallenge(null);
    setReplacementProofType('password');
  };

  return (
    <>
    <Dialog open={loginOpen} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 600 }}>
        Sign in
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <TextField
            label="Email"
            type="email"
            fullWidth
            value={email}
            onChange={e => setEmail(e.target.value)}
          />
          <TextField
            label="Password"
            type="password"
            fullWidth
            value={password}
            onChange={e => setPassword(e.target.value)}
          />
          <Button
            variant="text"
            size="small"
            sx={{
              textDecoration: 'none',
              fontSize: '0.875rem',
              color: 'primary.main',
              '&:hover': { textDecoration: 'underline' },
              justifyContent: 'flex-start',
              pl: 0
            }}
          >
            Forgot Password?
          </Button>
        </Box>
      </DialogContent>
      <Box sx={{ px: 2, py: 2 }}>
        <Divider sx={{ my: 1, mb: 2 }}>OR</Divider>
          <Button
          variant="outlined"
          fullWidth
          onClick={() => {
            const googleParams = new URLSearchParams({
              source: 'general',
              redirect: '/auth/callback'
            });
            window.location.href = `${API_BASE_URL}/auth/google?${googleParams.toString()}`;
          }}
          sx={{ mb: 2, textTransform: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}
          startIcon={<img src="https://www.svgrepo.com/show/475656/google-color.svg" alt="Google" style={{ height: '20px', width: '20px' }} />}
        >
          Sign in with Google
        </Button>
      </Box>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button variant="text" onClick={handleClose}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={loading}>
          {loading ? 'Signing in...' : 'Sign in'}
        </Button>
      </DialogActions>
      <Box sx={{ px: 2, pb: 2, textAlign: 'center' }}>
        <Typography variant="body2">
          Don't have an account?{' '}
          <Button
            variant="text"
            size="small"
            onClick={switchToSignup}
            sx={{ textTransform: 'none', fontSize: 'inherit' }}
          >
            Create one
          </Button>
        </Typography>
        <Box sx={{ mt: 2, textAlign: 'left' }}>
          <AuthLegalNotice />
        </Box>
      </Box>
    </Dialog>
    <SessionReplacementDialog
      open={Boolean(replacementChallenge)}
      proofType={replacementProofType}
      loading={loading}
      error={error}
      onConfirm={confirmReplacement}
      onCancel={() => { setReplacementChallenge(null); setError(null); }}
    />
    </>
  );
};

export default LoginModal;
